import React, { useEffect, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { NotifyConfirm, Toast } from "../../../../components/notify/Notify";
import { compareTwoArrayDnD } from "../../../../utils/helper";
import useCurrentActionCache from "../../../../hooks/useCurrentActionCache";
import { CACHE_CATEGORY } from "../../../../constants/actionCache";
import {
  createFLFCategory,
  getFLFCategory,
  removeFLFCategory,
  sortFLFCategory,
  updateFLFCategory,
} from "../../../../apis/FLF/category.api";
import { path } from "../../../../constants/path";
import { useCallback } from "react";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import FLFCategoryControlPanel from "../../components/ControlPanel/FLFCategoryControlPanel";
import useControlPanel from "src/hooks/useControlPanel";
import { dataStateType } from "src/constants/status";
import CategoryTable from "../../components/Table/CategoryTable";

const initCategoryData = {
  list: [],
  total_record: 0,
};

const FLFCategories = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  // cache
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();

  const [categoryData, setCategoryData] = useState(initCategoryData);
  const [filterCategories, setFilterCategories] = useState([]);
  const [listCategoryId, setListCategoryId] = useState([]);
  const [queueSortApi, setQueueSortApi] = useState([]);
  const [isSorted, setIsSorted] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);

  const [errorsForm, setErrorsForm] = useState({});

  const fetchCategories = useCallback(() => {
    getFLFCategory({}, (result) => {
      setCategoryData({ ...result });
      setFilterCategories([...result.list]);
      setListCategoryId([...result.list?.map((c) => c.id)]);
    });
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredCategory = useCallback((categories) => {
    setFilterCategories(categories);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (categories, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return categories.filter((category) => {
        return category.name.toLowerCase().includes(key.toLowerCase().trim());
      });
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredCategories = value
        ? searchByKeyWord(categoryData.list, value)
        : categoryData.list;
      updateFilteredCategory(filteredCategories);
    },
    [categoryData.list, searchByKeyWord, updateFilteredCategory]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (category, resetForm) => {
      if (category.id) {
        // Update existing partner
        const payload = {
          name: category.name,
          rogoDevTypeCode: category.rogoDevTypeCode,
          rogoDevTypeStr: category.rogoDevTypeStr,
          rogoDevSubTypeIds: category.rogoDevSubTypeIds,
        };
        updateFLFCategory(category.id, payload, handleApiResponse);
      } else {
        // Create new partner
        createFLFCategory(category, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchCategories();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_CATEGORY);
        }
      }
    },
    [fetchCategories, setCurrentActionCache]
  );

  /* ---- handle sort and DnD ----- */
  const handleSort = useCallback(() => {
    sortFLFCategory({ queueSort: queueSortApi }, () => {
      setQueueSortApi([]);
      setIsSorted(false);
      setCurrentActionCache(CACHE_CATEGORY);
      fetchCategories();
    });
  }, [queueSortApi, setCurrentActionCache, fetchCategories]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // kiểm tra nếu không tồn tại over (kéo quá size thì return tránh lỗi)
    if (!over) return;

    if (active.id !== over.id) {
      // lấy vị trí cũ (từ thằng active)
      const oldIndex = filterCategories.findIndex((c) => c.id === active.id);

      // lấy vị trí mới (từ thằng order)
      const newIndex = filterCategories.findIndex((c) => c.id === over.id);

      const newOrder = arrayMove(filterCategories, oldIndex, newIndex);
      const newOrderIds = newOrder.map((c) => c.id);

      // check xem có thay đổi thì hiện nút xác nhận cập nhật
      const isOrderChanged = !compareTwoArrayDnD(listCategoryId, newOrderIds);
      setFilterCategories([...newOrder]);
      if (isOrderChanged) {
        setIsSorted(true);
        setQueueSortApi((prev) => [
          ...prev,
          {
            activeCategoryId: active.id,
            overCategoryId: over.id,
          },
        ]);
      } else {
        setIsSorted(false);
        setQueueSortApi([]);
      }
    }
  };

  const handleDelete = useCallback(
    (categoryDeleted) => {
      if (categoryDeleted.devices.length) {
        Toast("warning", "Category vẫn còn thiết bị, không thể xóa");
      } else {
        NotifyConfirm("warning", "Are you delete it ?", (result) => {
          if (result.isConfirmed) {
            removeFLFCategory(categoryDeleted.id, (result) => {
              setCurrentActionCache(CACHE_CATEGORY);
              fetchCategories();
            });
          }
        });
      }
    },
    [fetchCategories, setCurrentActionCache]
  );

  const handleDisplayHomeScreen = (item) => (event) => {
    const { value } = event.target;

    NotifyConfirm("warning", "Are you change it ?", (result) => {
      if (result.isConfirmed) {
        const isDisplayHomeScreenUpdate = value === "show" ? true : false;
        updateFLFCategory(
          item.id,
          { ...item, isDisplayHomeScreen: isDisplayHomeScreenUpdate },
          (_) => {
            fetchCategories();
            setRecordForEdit(null);
            setOpenPopup(false);
            setCurrentActionCache(CACHE_CATEGORY);
          }
        );
      }
    });
  };

  // ==== refactor ====

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  /* ---- result for render ----- */

  const resultFilter = useMemo(
    () => searchByKeyWord(filterCategories, searchKey),
    [filterCategories, searchByKeyWord, searchKey]
  );

  const listItem = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{"FLF Categories"}</h2>
      </div>

      <FLFCategoryControlPanel
        handleOpenPopup={handleOpenPopup}
        isSorted={isSorted}
        handleSort={handleSort}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFCategoryControlPanel>

      <CategoryTable
        data={listItem}
        handleOpenPopup={handleOpenPopup}
        handleDisplayHomeScreen={handleDisplayHomeScreen}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
        onDragEnd={handleDragEnd}
      />

      <Pagination
        pathname={path.flfCategory}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFCategories;
